import React from "react";
import User from '~/model/user';
import { Store } from 'react-notifications-component';
import config from '~/config';
import UserRole from '~/model/userRole';
import Order from '~/model/order';
import DOMPurify from 'dompurify';
import { subtractHoursFromNow } from './dateHelper';
import { routes } from './routeHelper';
import OrderNotary from '~/model/orderNotary';
import DealerStore from "../model/dealerStore";
import regx from '~/constants/regx';
import { WEEKDAYS } from '~/constants/weekday';
import { UncontrolledTooltip } from "reactstrap";
import pendingVidIcon from "../assets/images/pending-VID.svg";
import skippedVidIcon from "../assets/images/skipped-VID.svg";
import failedVid from "../assets/images/failed-VID.svg";
import passedVid from "../assets/images/passed-VID.svg";
import VidRequest from "../model/vidRequest";
import AdditionalFee from "~/model/additionalFee";
import AddOn from "~/model/addOn";

export const sleep = time => new Promise((resolve) => setTimeout(resolve, time));

export const getSharedPaginationOptions = () => ({
  custom: true,
  hidePageListOnlyOnePage: true,
  paginationSize: 10,
  sizePerPageList: [
    { text: '1', value: 1 },
    { text: '10', value: 10 },
    { text: '50', value: 50 },
    { text: '100', value: 100 },
    { text: '200', value: 200 },
  ],
});

export const getSharedTableOptions = () => ({
  keyField: "id",
  bordered: false,
  striped: false,
  defaultSortDirection: "asc",
  selectRow: {
    mode: 'radio',
    hideSelectColumn: true,
  },
  classes: "table align-middle table-nowrap",
  headerWrapperClasses: "thead-light",
  responsive: true,
  remote: true,
});

export const valueIsDefined = value => value !== undefined && value !== null;

export const valueIsEmpty = value => {
  if (typeof value === 'string') {
    value = value.trim();
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  // allow 0 and FALSE
  return value !== 0 && value !== false && !value;
}

export const mapNonEmpty = obj => Object.entries(obj).reduce((a, [k, v]) => (!valueIsEmpty(v) ? (a[k] = v, a) : a), {});

export const hasNonEmpty = obj => Object.keys(mapNonEmpty(obj)).length > 0;

// recognizes real booleans and the literal strings "true"/"false"
export const isBooleanValue = value => {
  if (typeof value === 'boolean') {
    return true;
  }
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === 'false';
};

// converts a real boolean or the literal strings "true"/"false" to a boolean
export const toBoolean = value => {
  if (typeof value === 'boolean') {
    return value;
  }
  return String(value).trim().toLowerCase() === 'true';
};

// like valueIsEmpty, but also treats the literal strings "null"/"undefined" as empty
export const isBlankValue = value =>
  valueIsEmpty(value) || ['null', 'undefined'].includes(String(value).trim().toLowerCase());

// renders a value for display: empty -> em dash, objects/arrays stringified, else String
export const formatDisplayValue = value => {
  if (isBlankValue(value)) {
    return '--';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

// axios serializes only plain objects
// so any nested objects will end up as json
// but we need the filters as a query string array
// so here we do the transformation
export const flattenFilters = params => {
  // clone the object to avoid changing the original
  const newParams = { ...params };
  if (newParams.filters) {
    // loop through all filters
    for (const [key, value] of Object.entries(newParams.filters)) {
      // if the filter value is not empty
      if (!valueIsEmpty(value)) {
        // add the filter under the root object
        // change the key so it will be decoded properly on the backend
        newParams[`filters[${key}]`] = value;
      }
    }
    // remove the filters key since it is no longer needed
    delete newParams.filters;
  }
  return newParams;
}

// the Select control expects a list of {value, label} objects as options
// most of the time api response is different than what the Select control expects
// so here we do the transformation
export const toSelectOptions = (list, valueProp = 'id', labelProp = 'name') => {
  return list?.map(item => ({
    label: item[labelProp],
    value: item[valueProp],
  }));
}

export const getYesNoOptions = (includeAll = false) => {
  const options = [{
    label: "Yes",
    value: true,
  }, {
    label: "No",
    value: false,
  }];
  if (includeAll) {
    options.unshift({
      label: "All",
      value: '',
    });
  }
  return options;
}

export const getYesNoOptionsAsNumbers = (includeAll = false) => {
  const options = [{
    label: "Yes",
    value: 1,
  }, {
    label: "No",
    value: 0,
  }];
  if (includeAll) {
    options.unshift({
      label: "All",
      value: '',
    });
  }
  return options;
}

export const getOneTwoOptionsAsNumbers = () => {
  const options = [{
    label: "None",
    value: -1
  }, {
    label: "One",
    value: 1,
  }, {
    label: "Two",
    value: 2,
  }, {
    label: "All",
    value: 0,
  }];
  return options;
}

export const getDocumentDeliveryMethod = () => {
  const options = [{
    label: "Upload",
    value: Order.DOC_DELIVERY_OPTION_UPLOAD,
  }, {
    label: "Shipping",
    value: Order.DOC_DELIVERY_OPTION_SHIPPING,
  }];
  return options;
}

export const getActiveOptions = () => {
  const options = [{
    label: "Active",
    value: true,
  }, {
    label: "Disabled",
    value: false
  }];
  return options;
}

export const distanceServedOptions = [
  {
    value: 10,
    label: "10 miles radius"
  },
  {
    value: 20,
    label: "20 miles radius"
  },
  {
    value: 30,
    label: "30 miles radius"
  },
  {
    value: 40,
    label: "40 miles radius"
  },
  {
    value: 50,
    label: "50 miles radius"
  },
];

export const canPayWithCheckOptionsForGroups = [
  { value: true, label: "Enable check payments for the stores within this group" },
  { value: false, label: "Do not enable check payments for the stores within this group" },
];

export const canPayWithCheckOptionsForStores = [
  { value: true, label: "Enable check payments for this store" },
  { value: false, label: "Do not enable check payments for this store" },
];

export const isObject = obj => typeof obj === 'object' && obj !== null;

export const showNotification = (type, message, options) => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 6000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showBriefNotification = (type, message, options) => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 2000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showSuccess = (message, options) => showNotification('success', message, options);

export const showBriefSuccess = (message, options) => showBriefNotification('success', message, options);

export const showMessage = (message, options) => showNotification('info', message, options);

export const showWarning = (message, options) => showNotification('warning', message, options);

export const showError = (message, options) => showNotification('danger', message, options);

export const showBriefError = (message, options) => showBriefNotification('danger', message, options);

export const removeNotification = id => Store.removeNotification(id);

export const getChangeEventData = (e, meta) => {
  let name, value;
  if (e.target) {
    name = e.target.name;
    value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
  } else {
    // meta is only available for Select
    name = meta.name;
    // the Select component only accepts an object as value
    // so here we return the entire selected option
    value = e;
  }
  return [name, value];
}

export const trim = (str, char) => {
  char = char.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return str.replace(new RegExp(
    "^[" + char + "]+|[" + char + "]+$", "g"
  ), "");
}

export const ltrim = (s, c) => {
  c = c.replace(/[-/$*+?.()|[]{}]/g, '&');
  return s.replace(new RegExp(
    "^[" + c + "]+", "g"
  ), "");
}

export const rtrim = (s, c) => {
  c = c.replace(/[-/$*+?.()|[]{}]/g, '&');
  return s.replace(new RegExp(
    "[" + c + "]+$", "g"
  ), "");
}

export const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);

export const buildUrl = (base, fragment) => rtrim(base, '/') + (fragment ? '/' + ltrim(fragment, '/') : '');

export const getBeUrl = fragment => buildUrl(config.API_BE_URL, fragment);

export const getSsoAppUrl = fragment => buildUrl(config.SSO_URL, fragment);

export const getDealerAppUrl = fragment => buildUrl(config.DEALER_URL, fragment);

export const getNotaryAppUrl = fragment => buildUrl(config.NOTARY_URL, fragment);

export const nullsToEmptyStrings = (obj, recursive = false, excludeKeys = []) => {
  if (!obj) {
    return;
  }
  if (Array.isArray(obj)) {
    return obj.map((val, idx) => {
      if (excludeKeys.includes(idx)) {
        return val;
      }
      if (val === null) {
        return '';
      } else if (typeof val === 'object' && recursive) {
        return nullsToEmptyStrings(val, recursive, excludeKeys);
      }
      return val;
    });
  } else {
    return Object.fromEntries(Object.entries(obj).map(([key, val]) => {
      if (excludeKeys.includes(key)) {
        return [key, val];
      }
      if (val === null) {
        return [key, ''];
      } else if (typeof val === 'object' && recursive) {
        return [key, nullsToEmptyStrings(val, recursive, excludeKeys)];
      }
      return [key, val];
    }));
  }
}

export const isNotAllOrNone = value => value != '' && value != -1;

export const toUserModel = user => Object.assign(new User(), user);

const US_TIMEZONES = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
];

const getTimezoneAbbrIntl = zone =>
  new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName')?.value ?? zone;

export const getTimezoneOptions = () => US_TIMEZONES.map(zone => ({
  label: `${zone} (${getTimezoneAbbrIntl(zone)})`,
  value: zone,
}));

export const getTimezoneAbbr = zone => getTimezoneAbbrIntl(zone);

export const getUserOwner = user => {
  const owner = {
    name: null,
    id: null,
    route: null,
    type: null,
  };
  if (UserRole.isAdminType(user.userRoleId)) {
    owner.name = config.APP_TITLE;
    owner.type = 'mav';
  } else if (UserRole.isDealerGroupManager(user.userRoleId)) {
    owner.name = user.dealerGroupName;
    owner.id = user.dealerGroupId;
    owner.route = routes.view_dealer_group;
    owner.type = 'dealerGroup';
  } else if (UserRole.isDealerRegionalManager(user.userRoleId)) {
    owner.name = user.dealerRegionName;
    owner.id = user.dealerRegionId;
    owner.route = routes.view_dealer_region;
    owner.type = 'dealerRegion';
    owner.parentName = user.dealerGroupName;
    owner.parentId = user.dealerGroupId;
    owner.parentRoute = routes.view_dealer_group;
  } else if (UserRole.isDealerType(user.userRoleId)) {
    owner.name = user.dealerStoreName;
    owner.id = user.dealerStoreId;
    owner.route = routes.view_dealer_store;
    owner.type = 'dealerStore';
  } else if (UserRole.isNotaryType(user.userRoleId)) {
    owner.name = user.fullName;
    owner.id = user.notaryId;
    owner.route = routes.view_notary;
    owner.type = 'notary';
  }
  return owner;
}

export const withAuth = fragment => buildUrl(config.AUTH_PATH_PREFIX, fragment);

export const randomString = len => new Promise((resolve, reject) => {
  try {
    const array = new Uint8Array(len);
    window.crypto.getRandomValues(array);
    const hex = Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    resolve(hex);
  } catch (error) {
    reject(null);
  }
});

export const randomStringSync = (len) => {
  const array = new Uint8Array(len);
  window.crypto.getRandomValues(array);

  return Array.from(array)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

export const mergeRefs = (...refs) => {
  const filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return inst => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref) {
        ref.current = inst;
      }
    }
  };
};

export const dictionaryToSelectOptions = dict => Object.entries(dict).map(entry => ({ label: entry[1], value: +entry[0] }));

export const orderIsLocked = sealStatus => [Order.SEAL_STATUS_SEALED, Order.SEAL_STATUS_QUEUED].includes(sealStatus);

export const formatPhone = phone => {
  if (!phone) {
    return;
  }
  const clean = phone.replace(/\D/g, '');
  const match = clean.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match) {
    return clean;
  }
  let result = '';
  if (!!match[1]) {
    result += match[1];
  }
  if (!!match[2]) {
    result += '-' + match[2];
  }
  if (!!match[3]) {
    result += '-' + match[3];
  }
  return result;
}

export const openInNewWindow = url => {
  // Safari is blocking any call to open a new window which is made inside an async call, treating it as a pop-up.
  // setTimeout code runs on the main thread, instead of the asynchronous one
  setTimeout(() => {
    const a = document.createElement('a');
    a.target = '_blank';
    a.href = url;
    a.click();
  })
}

export const sanitizeHtml = html => DOMPurify.sanitize(html);

export const stripTags = value => value?.replace(/(<([^>]+)>)/gi, "");

export const getInitialsFromName = name => {
  if (!name) {
    return null;
  }
  return name.match(/\b(\w)/g).join('');
}

export const getLogAuthorFullName = author => {
  if (author === null) {
    return 'System';
  }
  if (!author || !isObject(author)) {
    return 'Unknown';
  }
  if (!!author.fullName) {
    return author.fullName;
  }
  const nameParts = [];
  if (!!author.firstName) {
    nameParts.push(author.firstName);
  }
  if (!!author.lastName) {
    nameParts.push(author.lastName);
  }
  if (nameParts.length > 0) {
    return nameParts.join(' ');
  }
}

export const filterObjectKeys = (obj, keys) => {
  if (!obj) {
    return null;
  }
  return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)));
}

export const extractDtFiltersFromUrl = () => {
  const query = new URLSearchParams(window.location.search);
  const filters = {};
  for (const [key, value] of query.entries()) {
    const match = key.match(/filters\[(.+)\]/);
    if (!!match && !!match[1]) {
      filters[match[1]] = value;
    }
  }
  return filters;
}

export const removeDtFiltersFromUrl = () => {
  const query = new URLSearchParams(window.location.search);
  const filters = {};
  for (const [key, value] of query.entries()) {
    const match = key.match(/filters\[(.+)\]/);
    if (!match) {
      filters[key] = value;
    }
  }
  return new URLSearchParams(filters).toString();
}

export const getInactiveOrdersFilters = () => ({
  inactive: subtractHoursFromNow(24)
});

export const bytesToSize = bytes => {
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return 'n/a'
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10)
  if (i === 0) return `${bytes} ${sizes[i]})`
  return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`
}

export const getAddressComponents = place => {
  const addressComponents = {
    address: "",
    city: "",
    state: "",
    zip: "",
  };
  // get each component of the address from the place details,
  // and then fill-in the corresponding field on the form.
  // place.address_components are google.maps.GeocoderAddressComponent objects
  // which are documented at http://goo.gle/3l5i5Mr
  for (const component of place.address_components) {
    const types = component.types;

    if (types.includes("street_number")) {
      addressComponents.address = `${component.long_name} ${addressComponents.address}`;
    }
    if (types.includes("route")) {
      addressComponents.address += component.short_name;
    }
    if (types.includes("postal_code")) {
      addressComponents.zip = component.long_name;
    }
    if (types.includes("locality")) {
      addressComponents.city = component.long_name;
    }
    if (types.includes("administrative_area_level_1")) {
      addressComponents.state = component.short_name;
    }
  }
  return addressComponents;
}

export const getMiles = meters => (meters * 0.000621371192).toFixed(2);

export const decimalToDMS = (decimal, isLatitude) => {
  const degrees = Math.floor(Math.abs(decimal));
  const decimalMinutes = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(decimalMinutes);
  const seconds = (decimalMinutes - minutes) * 60;

  let direction = "";
  if (isLatitude) {
    direction = decimal >= 0 ? 'N' : 'S';
  } else {
    direction = decimal >= 0 ? 'E' : 'W';
  }

  return `${degrees}° ${minutes}' ${seconds.toFixed(2)}" ${direction}`;
}

export const getGranularStatusName = (status, notaryAssignmentMode = null) => {
  if (!status) return

  const [statusType, statusId] = status.split("_").map(id => parseInt(id));

  let statusMap;

  switch (statusType) {
    case Order.GRANULAR_STATUS_TYPE_INTERNAL:
      statusMap = Order.getInternalStatusMap();
      break;
    case Order.GRANULAR_STATUS_TYPE_ORDER_NOTARY:
      statusMap = OrderNotary.getStatusMap();
      break;
    case Order.GRANULAR_STATUS_TYPE_ORDER:
    default:
      statusMap = Order.getStatusMap(notaryAssignmentMode);
      break;
  }

  return statusMap[statusId];
}

export const getGranularStatusOption = (type, status) => {
  const granularStatus = `${type}_${status}`;
  return ({ value: granularStatus, label: getGranularStatusName(granularStatus) });
};

export const getGranularStatusOptions = () => ([
  ...Object.keys(Order.getStatusMap()).map(status => getGranularStatusOption(Order.GRANULAR_STATUS_TYPE_ORDER, status)),
  ...Object.keys(OrderNotary.getStatusMap()).map(status => getGranularStatusOption(Order.GRANULAR_STATUS_TYPE_ORDER_NOTARY, status)),
]);

export const getGranularInStoreStatusOptions = () => ([
  ...Object.keys(Order.getInStoreStatusMap()).map(status => getGranularStatusOption(Order.GRANULAR_STATUS_TYPE_ORDER, status))
]);

export const getYesNoLabel = value => {
  switch (value) {
    case 0:
      return 'No'
    case 1:
      return 'Yes'
  }
}

export const newLineTextFormat = (lines) => {
  // Add each line of text on new line
  // and add bullet symbol at the beginning
  let formattedText = lines?.map(function (line) {
    return '\u2022' + ' ' + line;
  });
  return formattedText;
}

export const getPaymentMethod = type => {
  switch (type) {
    case DealerStore.PAYMENT_METHOD_TYPE_CARD:
      return 'Credit Card'
    case DealerStore.PAYMENT_METHOD_TYPE_BANK_ACCOUNT:
      return 'Bank Account'
    case DealerStore.PAYMENT_METHOD_TYPE_CHECK:
      return 'Check'
  }
}

export const getTrendColor = percentage => {
  if (percentage > 0) return "success";
  if (percentage < 0) return "danger";
  if (percentage === 0) return "warning";
  return "secondary";
};

export const getDealerFeeTotalCost = fees => {
  let total = 0;

  fees?.forEach((fee) => {
    const feeId = parseInt(fee.additionalFeeId);
    const price = parseFloat(fee.price);
    const count = fee.count || 1;

    const isNegative = AdditionalFee.getNegativeFees().includes(feeId);

    const feeTotal = price * count;

    if (isNegative) {
      total -= feeTotal;
    } else {
      total += feeTotal;
    }
  });

  if (total < 0) {
    return 0;
  }

  return total;
};

// styles for smaller size Select
export const smallSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  clearIndicator: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

export const getSigningFeeServiceDesc = customerLocation => {
  switch (customerLocation) {
    case Order.CUSTOMER_LOCATION_REMOTE:
      return 'Remote Transaction Fee';
    case Order.CUSTOMER_LOCATION_IN_STORE:
      return 'In-Store Transaction Fee';
    default:
      return '--';
  }
}

export const phoneHasNoOfDigits = (value) => {
  return !!value && value.replace(regx.phoneChars, '').length == config.PHONE_NR_OF_DIGITS;
}

export const getVIDStatusImage = (vidStatus, row) => {
  if (vidStatus === VidRequest.STATUS_CREATED ||
    vidStatus === VidRequest.STATUS_PENDING ||
    vidStatus === VidRequest.STATUS_ESCALATED ||
    vidStatus === null)
    return <><img id={row && `verify-id${row.id}`} src={pendingVidIcon} className="me-2" />{row && <UncontrolledTooltip placement="top" target={`verify-id${row.id}`}>Pending Verify ID</UncontrolledTooltip>}</>

  if (vidStatus === VidRequest.STATUS_SKIPPED)
    return <><img id={row && `verify-id${row.id}`} src={skippedVidIcon} className="me-2" />{row && <UncontrolledTooltip placement="top" target={`verify-id${row.id}`}>Skipped Verify ID</UncontrolledTooltip>}</>

  if (vidStatus === VidRequest.STATUS_ERROR ||
    vidStatus === VidRequest.STATUS_MITEK_API_FAIL ||
    vidStatus === VidRequest.STATUS_FAILED ||
    vidStatus === VidRequest.STATUS_PENDING_FAILED)
    return <><img id={row && `verify-id${row.id}`} src={failedVid} className="me-2" />{row && <UncontrolledTooltip placement="top" target={`verify-id${row.id}`}>Failed Verify ID</UncontrolledTooltip>}</>

  if (vidStatus === VidRequest.STATUS_PASSED)
    return <><img id={row && `verify-id${row.id}`} src={passedVid} className="me-2" />{row && <UncontrolledTooltip placement="top" target={`verify-id${row.id}`}>Verify ID</UncontrolledTooltip>}</>
}

// combines the active vid request statuses of the signers the same way the order 'vidStatus' is computed on the backend
// so the icon matches the one from the order listing and details
// the VID service is passed only when all the signers have passed the VID
export const getCombinedVidStatus = statuses => {
  statuses = statuses.filter(status => status !== null && status !== undefined);
  if (!statuses.length) {
    return VidRequest.STATUS_PENDING;
  }
  if (statuses.every(status => status === VidRequest.STATUS_PASSED)) {
    return VidRequest.STATUS_PASSED;
  }
  if (statuses.some(status => status === VidRequest.STATUS_FAILED)) {
    return VidRequest.STATUS_FAILED;
  }
  if (statuses.every(status => status === VidRequest.STATUS_SKIPPED)) {
    return VidRequest.STATUS_SKIPPED;
  }
  return VidRequest.STATUS_PENDING;
}

export const getDtFooterRowCount = (paginationProps, numOfVisibleRows, totalNumOfRows) => {
  const startNum = ((paginationProps.page - 1) * paginationProps.sizePerPage) + 1;
  const endNum = ((paginationProps.page - 1) * paginationProps.sizePerPage) + numOfVisibleRows;
  return <>
    Showing <span id="dt-rowcount">{numOfVisibleRows}</span> of {totalNumOfRows} items
    <UncontrolledTooltip placement="top" target="dt-rowcount">{startNum}-{endNum}</UncontrolledTooltip>
  </>;
}

export const getBounceReason = (reason) => {
  switch (reason) {
    case User.EMAIL_ALERT_GENERAL:
      return <span>It seems the email address associated with your account does not exist. Please update it in your profile.</span>;
    case User.EMAIL_ALERT_MAIL_BOX_FULL:
      return <span>It seems your inbox is full and cannot receive any more emails. Please clear it out.</span>;
    case User.EMAIL_ALERT_ON_ACCOUNT_SUPPRESSION_LIST:
      return <span>It seems the Mavsign email address is blocked/marked as spam by you. Please adjust that in your email platform.</span>;
    default:
      return <span>There seems to be an unknown issue with your email. Please check your email settings or contact support.</span>;
  }
};

export const checkReasonType = (user) => {
  // check if the user email has bounceSubtype or complaintSubtype
  // based on this, display the according email bounce reason message from above
  const subtype = user.bounceSubtype || user.complaintSubtype;
  return getBounceReason(subtype);
}

export const getDealerNotaryBounceReason = (reason) => {
  switch (reason) {
    case User.EMAIL_ALERT_GENERAL:
      return <span>It seems the email address associated with this account does not exist.</span>;
    case User.EMAIL_ALERT_MAIL_BOX_FULL:
      return <span>It seems the inbox is full and cannot receive any more emails.</span>;
    case User.EMAIL_ALERT_ON_ACCOUNT_SUPPRESSION_LIST:
      return <span>It seems the Mavsign email address is blocked/marked as spam by the dealer.</span>;
    default:
      return <span>It seems to be an unknown issue with the email.</span>;
  }
};

export const checkDealerNotaryBounceReason = (bounceAlert) => {
  // check if the user email has bounceSubtype or complaintSubtype
  // based on this, display the according email bounce reason message from above
  const subtype = bounceAlert.bounceSubtype || bounceAlert.complaintSubtype;
  return getDealerNotaryBounceReason(subtype);
}

export const formatIpAddress = ipAddress => {
  return !!ipAddress ? ipAddress.replace(/^::ffff:/, '') : ipAddress;
}

// regex to check if the message has tagged people in it
const hasTaggedPeople = /(?<!id=")@\[([A-z0-9\s'-.,()"`]+)\]\((\d+)-(\d+)\)/g;
// regex to check if the massage has links
const urlRegex = /((https?:\/\/|www\.)[^\s]+)/gi;

// Function that formats a message by replacing tags and URLs
export const formatMessage = (value) => {
  if (!value) return "";

  // Replace @tags like @[user_id:username]
  const tagReplacement = (match) => {
    const label = match.substring(2, match.indexOf(']'));
    return `<span contentEditable='false'
    style='display:inline-block; margin-top:1px; color: #556EE6; font-weight: 600; background-color:#dfdfdf; border-radius: 20px; padding: 0px 5px; font-size:12px;'
    spellcheck='false'
    id='${match}'>@${label}</span>&nbsp;`;
  };

  // Replace URLs
  const urlReplacement = (match) => {
    const href = match.startsWith('www.') ? `https://${match}` : match;
    return `<a href="${href}" target="_blank" style="color: #3b82f6;">${match}</a>`;
  };

  // Step-by-step replacements
  let formatted = value;

  // Replace tagged people
  formatted = formatted.replace(hasTaggedPeople, tagReplacement);

  // Replace URLs
  formatted = formatted.replace(urlRegex, urlReplacement);

  return formatted;
};

// Function that replaces tag template with the span
export const replaceTagsWithText = (value) => {
  return value?.replace(hasTaggedPeople, (match) => {
    return `@${match.substring(2, match.indexOf(']'))}`
  })
}

export const formatAccountingReviewReason = (reason) => {
  let reviewReason = [];
  if (typeof reason === "string") {
    return reason;
  } else {
    reason.forEach((reason) => {
      if (typeof reason === "number") {
        switch (reason) {
          case Order.ACCOUNTING_REVIEW_REASON_ADDITIONAL_VEHICLE:
            reviewReason.push('Additional Vehicles')
            break;
          case Order.ACCOUNTING_REVIEW_REASON_DEALER_FEES:
            reviewReason.push(`Dealer Fees > ${config.ACCOUNTING_REVIEW_DEALER_AMOUNT_THRESHOLD}`)
            break;
          case Order.ACCOUNTING_REVIEW_REASON_NOTARY_FEES:
            reviewReason.push(`Notary Fees > ${config.ACCOUNTING_REVIEW_NOTARY_AMOUNT_THRESHOLD}`)
            break;
          default:
            break;
        }
      } else {
        reviewReason.push(reason)
      }
    })

    return reviewReason.join(" , ");
  }
}

export const getDealerInstructionsText = (dealerInstructions, caDeliveryForm) => {
  if (!dealerInstructions && !caDeliveryForm) return "--";

  if ([Order.CA_DELIVERY_FORM_YES_BOTH_CONTRACTS, Order.CA_DELIVERY_FORM_YES].includes(caDeliveryForm)) {
    return `${!!dealerInstructions ? dealerInstructions : ""} ${Order.getCalifornia448SpecialInstruction()[caDeliveryForm]}`
  }
  return dealerInstructions;

}

export const formatAddonConfigTextLong = (addon) => {
  switch (addon.type) {
    case AddOn.TYPE_DISCOUNTED_REMOTE_VID:
      return `$${addon.config.remoteVidFee}/per transaction`
    case AddOn.TYPE_DISCOUNTED_DL_CHECK:
      return `$${addon.config.instoreVidFee}/per transaction`
    case AddOn.TYPE_INCLUDED_DL_CHECK:
      return `${addon.config.includedTransactions} Transactions included`
    case AddOn.TYPE_INCLUDED_REMOTE_VID:
      return `${addon.config.includedTransactions} Transactions included`
    case AddOn.TYPE_ENABLE_GUARANTEE:
      return `Included`
    case AddOn.TYPE_SECURE_OFFSITE_CLOSING:
      return <div className="text-align-left">
        <div>
          <span> &#8226; </span>
          ${addon.config.secureOffsiteClosingFee} Secure Offsite Closing
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.previewDocumentsFee} Preview Document Fee
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.inkSignFee} InkSign Fee
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.fedexGenerateLabelProcessingFee} Label Processing Fee
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.r1Fee} RouteOne Fee
        </div>
      </div>
    case AddOn.TYPE_INCLUDED_SECURE_OFFSITE_CLOSING:
      return `${addon.config.includedTransactions} Transactions included`
    case AddOn.TYPE_DISCOUNTED_ESIGN:
      return `$${addon.config.eSignFee}/per transaction`
    case AddOn.TYPE_INCLUDED_ESIGN:
      return `${addon.config.includedTransactions} Transactions included`
    case AddOn.TYPE_FLEET:
      return `${addon.config.vehicleThreshold} Vehicles Threshold, $${addon.config.additionalVehicleFee} Additional Vehicle Fee`
    case AddOn.TYPE_MAV_PRIME:
      return <div className="text-align-left">
        <div>
          <span> &#8226; </span>
          ${addon.config.mavPrimeFee} Mav Prime
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.rushFee} Rush Fee
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.insuranceVerificationFee} Insurance Verification Fee
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.previewDocumentsFee} Preview Documents Fee
        </div>
        <div>
          <span> &#8226; </span>
          ${addon.config.sameDayCompletionFee} Same Day Completion Fee
        </div>
      </div>
    case AddOn.TYPE_INSTORE_INSURANCE_VERIFICATION:
      return `$${addon.config.insuranceVerificationFee} Insurance Verification Fee`
    default:
      return '--'
  }
}

export const getAccountingServiceType = (customerLocation, isNotaryRequired) => {

  if (customerLocation === Order.CUSTOMER_LOCATION_REMOTE && isNotaryRequired) return 'M1';

  if (customerLocation === Order.CUSTOMER_LOCATION_REMOTE && !isNotaryRequired) return 'M2';

  if (customerLocation === Order.CUSTOMER_LOCATION_IN_STORE && !isNotaryRequired) return 'M3';

  return '--';
}

/********** WORKING HOURS **********/

export const getDefaultWorkingHours = () => WEEKDAYS.map(day => ({
  day,
  startTime: '',
  endTime: '',
  isDayOff: false,
}));

export const parseWorkingInterval = (workingInterval) => {
  let parsed = workingInterval;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return getDefaultWorkingHours();
    }
  }
  if (Array.isArray(parsed) && parsed.length === 7) {
    return parsed;
  }
  return getDefaultWorkingHours();
};
